import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';

import { RoutesManager } from 'src/app/shared/models/routes.interfaces';
import { environment } from 'src/environments/environment';
import { CollaborationTokenStoreService } from './collaboration-token-store.service';
import { CollaborationLoggerService } from './collaboration-logger.service';

@Injectable({
    providedIn: 'root',
})
export class CollaborationTokenService {
    private readonly logOptions = { component: 'CollaborationTokenService' };

    constructor(
        private tokenStore: CollaborationTokenStoreService,
        private logger: CollaborationLoggerService,
        private auth: AuthService,
    ) {}

    /**
     * Verifies that the Auth0 SDK session is active by calling getAccessTokenSilently().
     * The SDK handles caching and token refresh internally — the token is never stored by us.
     */
    async verifyTokenAndSession(): Promise<boolean> {
        this.logger.info('Verifying Auth0 session via getAccessTokenSilently', {}, this.logOptions);
        try {
            const token = (await firstValueFrom(
                this.auth
                    .getAccessTokenSilently({
                        authorizationParams: {
                            audience: environment.collaboration.auth0.audience,
                        },
                    })
                    .pipe(timeout(5_000)),
            )) as string;

            if (token) {
                this.logger.info('Auth0 session verified successfully', {}, this.logOptions);
                return true;
            }

            this.logger.warn('getAccessTokenSilently returned empty token', {}, this.logOptions);
            return false;
        } catch (error) {
            this.logger.warn(
                'Auth0 session invalid - getAccessTokenSilently threw',
                { error: (error as Error)?.message || String(error) },
                this.logOptions,
            );
            return false;
        }
    }

    loginWithRedirect(assessmentId: string, email?: string): void {
        const companyId = this.tokenStore.getCompanyId();
        const collaboratorEmail = email ?? this.tokenStore.getCollaboratorEmail();

        this.logger.info(
            'Initiating Auth0 passwordless login redirect',
            {
                assessmentId,
                redirectUri: `${window.location.origin}/${RoutesManager.collaborationAuthProxy}`,
            },
            this.logOptions,
        );

        const companyName = this.tokenStore.getCompanyName();

        const targetParams = new URLSearchParams();
        targetParams.set('authenticated', 'true');
        targetParams.set('assessmentPageId', assessmentId);
        if (companyId) targetParams.set('companyId', companyId);
        if (collaboratorEmail) targetParams.set('email', collaboratorEmail);
        if (companyName) targetParams.set('companyName', companyName);

        const deepLinkState = this.tokenStore.getDeepLinkState();
        if (deepLinkState?.openCommentsPanel) {
            targetParams.set('openCommentsPanel', '1');
            if (deepLinkState.sectionId) targetParams.set('sectionId', deepLinkState.sectionId);
            if (deepLinkState.assessmentQuestionId) targetParams.set('assessmentQuestionId', deepLinkState.assessmentQuestionId);
            if (deepLinkState.notesThreadId) targetParams.set('notesThreadId', deepLinkState.notesThreadId);
            if (deepLinkState.agentEmail) targetParams.set('agentEmail', deepLinkState.agentEmail);
        }

        this.auth.loginWithRedirect({
            authorizationParams: {
                audience: environment.collaboration.auth0.audience,
                connection: 'email',
                ...(collaboratorEmail && { login_hint: collaboratorEmail }),
                redirect_uri: `${window.location.origin}/${RoutesManager.collaborationAuthProxy}`,
            },
            appState: {
                target: `/${RoutesManager.collaborationAuthProxy}?${targetParams.toString()}`,
            },
        });
    }
}
